import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { PageContainer, RcsSimpleStepper, RcsSimpleStepperItem, RcsSimpleStepperItemStatus, TextArea, } from '@guildeducationinc/recess';
import { Attestations } from '@src/components/Employee/EditProgram/Attestations';
import { FormActions } from '@src/components/Employee/FormActions';
import { PaymentRequestMessages } from '@src/components/Employee/PaymentRequestMessages';
import { AboutPaymentRequest } from '@src/components/Employee/PaymentRequests/EditPaymentRequest/AboutPaymentRequest';
import { EditExpenseModal } from '@src/components/Employee/PaymentRequests/EditPaymentRequest/EditExpenseModal/EditExpenseModal';
import { Expenses } from '@src/components/Employee/PaymentRequests/EditPaymentRequest/Expenses';
import { PaymentRequestReview } from '@src/components/Employee/PaymentRequests/EditPaymentRequest/PaymentRequestReview/PaymentRequestReview';
import { FlowContainer } from '@src/components/FlowContainer';
import { TopBar } from '@src/components/TopBar';
import { ProgramContext } from '@src/contexts/ProgramContextProvider';
import { useCheckMyPaymentRequestDuplicates } from '@src/hooks/usePaymentRequestDuplicates';
import { PaymentRequestStep, useSavePaymentRequest } from '@src/hooks/useSavePaymentRequest';
import { PaymentMethod, PaymentRequestStatus, PreimbursementStatus, RuleType, } from '@src/types';
import { getActiveItems, getTotalRequested, isUploadDocumentRequired, validatePaymentRequest, } from '@src/utilities/paymentRequestHelper';
import { compact } from 'lodash';
import { getQueryParam, MessageType, trackEvent, useLocale, useUploadContext, } from 'pe-core-frontend';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styles from './EditPaymentRequest.module.scss';
export const EditPaymentRequest = ({ program, paymentRequest: initialPaymentRequest, }) => {
    const step = (getQueryParam('step') || PaymentRequestStep.ABOUT);
    const [currentStep, setCurrentStep] = useState(step);
    const { t, formatCurrency } = useLocale();
    const navigate = useNavigate();
    const [estCompDate, setEstCompDate] = useState(program.estCompDate);
    const [comment, setComment] = useState('');
    const { itemErrors, requestErrors, setRequestErrors, itemToEdit, errors, setErrors, setItemToEdit, updateItem, handleValidateItem, handleValidateRequest, save, handleAddItem, handleItemChange, handleItemRemove, paymentRequest, setPaymentRequest, resetItemToEdit, attestationContents, attestationContentIds, setAttestationContentIds, signature, setSignature, validateAttestations, eligibilityLock, } = useSavePaymentRequest({
        initialPaymentRequest,
    });
    const [submitting, setSubmitting] = useState(false);
    const { uploading } = useUploadContext();
    const activeItems = getActiveItems(paymentRequest);
    // Use custom hook for duplicate checking
    const { duplicateInfo, checkForDuplicates, resetDuplicateCheck } = useCheckMyPaymentRequestDuplicates({
        paymentRequest,
        program,
    });
    const showExpenses = !eligibilityLock.locked ||
        (eligibilityLock.canSubmitExpenses && paymentRequest.requiresExpenses);
    const initialDocuments = useRef(paymentRequest.documents.docs).current;
    const showUploadProof = paymentRequest.status === PaymentRequestStatus.AWAITING_DOCUMENTS ||
        (paymentRequest.paymentMethod === PaymentMethod.PREIMBURSEMENT &&
            paymentRequest.preimbursementStatus === PreimbursementStatus.PENDING_PROOF);
    const { getPolicy } = useContext(ProgramContext);
    const approvalPolicy = getPolicy(RuleType.APPROVAL_CHAIN, program);
    const documentRequired = useMemo(() => {
        return isUploadDocumentRequired(paymentRequest, approvalPolicy, t);
    }, [paymentRequest, approvalPolicy, t]);
    // call analytics on first render
    useEffect(() => {
        trackEvent('ReimbursementRequestStarted', {
            program_id: program.id,
        });
    }, []);
    // Trigger duplicate check when entering review step
    useEffect(() => {
        if (currentStep === PaymentRequestStep.REVIEW) {
            checkForDuplicates();
        }
    }, [currentStep, checkForDuplicates]);
    const isStepComplete = (step) => {
        if (step === PaymentRequestStep.REVIEW) {
            return false;
        }
        if (step === PaymentRequestStep.ABOUT) {
            const errors = validatePaymentRequest(paymentRequest, t, getPolicy, program, formatCurrency);
            return Object.keys(errors).length === 0;
        }
        return (activeItems.length > 0 &&
            getTotalRequested(paymentRequest) > 0 &&
            paymentRequest.documents.docs.length > 0);
    };
    const handleStepClick = (step) => (e) => {
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();
        e.preventDefault();
        // can go back to any step from review
        if (currentStep === PaymentRequestStep.REVIEW) {
            setCurrentStep(step);
            return false;
        }
        if (currentStep === PaymentRequestStep.EXPENSES && step === PaymentRequestStep.ABOUT) {
            setCurrentStep(step);
        }
        else {
            if (isStepComplete(currentStep)) {
                setCurrentStep(step);
            }
        }
        return false;
    };
    const handleSubmitPaymentRequest = async () => {
        setSubmitting(true);
        try {
            await save({ estCompDate, comment }, pmtReq => {
                trackEvent('ReimbursementRequestSubmitted', {
                    program_id: program.id,
                    reimbursement_id: pmtReq.id,
                });
                navigate(`/payments/${pmtReq.id}/submitted`);
            });
        }
        finally {
            setSubmitting(false);
        }
    };
    const handleOk = async () => {
        setErrors([]);
        if (paymentRequest.status === PaymentRequestStatus.ACTIVE &&
            !eligibilityLock.canSubmitExpenses) {
            return navigate('/');
        }
        if (currentStep === PaymentRequestStep.REVIEW) {
            if (!validateAttestations()) {
                return;
            }
            handleSubmitPaymentRequest();
        }
        else {
            if (!handleValidateRequest()) {
                return;
            }
            if (currentStep === PaymentRequestStep.EXPENSES) {
                if (documentRequired && paymentRequest.documents.docs.length === 0) {
                    setRequestErrors({ documents: t('Document is required') });
                    return;
                }
                if (eligibilityLock.canSubmitExpenses && activeItems.length === 0) {
                    setErrors([{ type: MessageType.ERROR, value: t('Expenses are required') }]);
                    return;
                }
                if (eligibilityLock.canSubmitExpenses && getTotalRequested(paymentRequest) <= 0) {
                    setErrors([
                        { type: MessageType.ERROR, value: t('Total amount requested must be greater than 0') },
                    ]);
                    return;
                }
            }
            if (currentStep === PaymentRequestStep.UPLOAD_PROOF) {
                if (documentRequired && paymentRequest.documents.docs.length === initialDocuments.length) {
                    setRequestErrors({ documents: t('Document is required') });
                    return;
                }
            }
            setSubmitting(true);
            try {
                // validate the request with the server
                const valid = await save({ estCompDate, validateOnly: true, currentStep });
                if (!valid)
                    return;
            }
            finally {
                setSubmitting(false);
            }
            if (currentStep === PaymentRequestStep.ABOUT) {
                if (showExpenses) {
                    setCurrentStep(PaymentRequestStep.EXPENSES);
                }
                else {
                    setCurrentStep(PaymentRequestStep.REVIEW);
                    resetDuplicateCheck();
                }
            }
            else if (currentStep === PaymentRequestStep.EXPENSES) {
                setCurrentStep(PaymentRequestStep.REVIEW);
                resetDuplicateCheck();
            }
            else if (currentStep === PaymentRequestStep.UPLOAD_PROOF) {
                setCurrentStep(PaymentRequestStep.REVIEW);
                resetDuplicateCheck();
            }
        }
    };
    const handleCancel = () => {
        if (currentStep === PaymentRequestStep.REVIEW) {
            if (showUploadProof) {
                setCurrentStep(PaymentRequestStep.UPLOAD_PROOF);
            }
            else if (showExpenses) {
                setCurrentStep(PaymentRequestStep.EXPENSES);
            }
            else {
                setCurrentStep(PaymentRequestStep.ABOUT);
            }
        }
        else if (currentStep === PaymentRequestStep.EXPENSES) {
            setCurrentStep(PaymentRequestStep.ABOUT);
        }
        else {
            navigate(-1);
        }
    };
    const steps = useMemo(() => {
        if (showUploadProof) {
            setCurrentStep(PaymentRequestStep.UPLOAD_PROOF);
        }
        return compact([
            !showUploadProof && 'about',
            !showUploadProof && showExpenses && 'expenses',
            showUploadProof && 'upload_proof',
            'review',
        ]);
    }, [showExpenses, showUploadProof]);
    return (_jsxs("div", { children: [_jsx(TopBar, { backLink: '/' }), _jsx(PageContainer, { children: _jsxs(FlowContainer, { children: [_jsxs("div", { className: styles.form, children: [_jsx("div", { className: 'mb-4', children: _jsx(RcsSimpleStepper, { className: styles.stepper, children: steps.map(stepName => (_jsx(RcsSimpleStepperItem, { LinkComponent: Link, href: '#', "aria-current": stepName === currentStep ? 'step' : false, status: isStepComplete(stepName)
                                                ? RcsSimpleStepperItemStatus.Completed
                                                : RcsSimpleStepperItemStatus.Incomplete, label: t(`PaymentRequestStep.${stepName}`), onClick: handleStepClick(stepName) }, stepName))) }) }), _jsxs("div", { className: styles.step, children: [_jsx(PaymentRequestMessages, { paymentRequest: paymentRequest, messages: errors, onClose: () => setErrors([]) }), _jsxs(_Fragment, { children: [currentStep === PaymentRequestStep.ABOUT && (_jsx(AboutPaymentRequest, { program: program, paymentRequest: paymentRequest, onChange: setPaymentRequest, requestErrors: requestErrors, estCompDate: estCompDate, setEstCompDate: setEstCompDate, eligibilityLock: eligibilityLock, errors: errors })), currentStep === PaymentRequestStep.EXPENSES && (_jsx(Expenses, { program: program, paymentRequest: paymentRequest, onChange: setPaymentRequest, onAdd: handleAddItem, onRemove: handleItemRemove, onEdit: setItemToEdit, requestErrors: requestErrors, errors: errors, documentRequired: documentRequired })), currentStep === PaymentRequestStep.UPLOAD_PROOF && (_jsx(Expenses, { title: t('Please provide proof of payment and completion'), readonly: true, uploadOnly: true, program: program, paymentRequest: paymentRequest, onChange: setPaymentRequest, onAdd: () => { }, onRemove: () => { }, onEdit: () => { }, requestErrors: requestErrors, errors: errors, documentRequired: documentRequired })), currentStep === PaymentRequestStep.REVIEW && (_jsxs(_Fragment, { children: [_jsx(PaymentRequestReview, { paymentRequest: paymentRequest, program: program, onEdit: setCurrentStep, estCompDate: estCompDate, eligibilityLock: eligibilityLock, readonly: showUploadProof, duplicateInfo: duplicateInfo }), paymentRequest.status === PaymentRequestStatus.INFO_REQUESTED && (_jsx("div", { className: 'my-4', children: _jsx(TextArea, { fullWidth: true, label: t('Add a comment'), helpText: t('Optional'), value: comment, onChange: e => setComment(e.currentTarget.value) }) })), attestationContents && attestationContents.length > 0 && (_jsx(Attestations, { contents: attestationContents, attestationContentIds: attestationContentIds || [], setAttestationContentIds: setAttestationContentIds, signature: signature, setSignature: setSignature, errors: requestErrors }))] }))] })] })] }), _jsx(FormActions, { showBackArrow: true, cancelText: t('Previous'), okText: paymentRequest.status === PaymentRequestStatus.ACTIVE &&
                                !eligibilityLock.canSubmitExpenses
                                ? t('Home')
                                : currentStep !== PaymentRequestStep.REVIEW
                                    ? t('Continue')
                                    : duplicateInfo.length > 0
                                        ? t('Submit Anyway')
                                        : t('Submit'), onCancel: handleCancel, onOk: handleOk, disabled: submitting || uploading }), itemToEdit && (_jsx(EditExpenseModal, { item: itemToEdit, onClose: resetItemToEdit, onChange: handleItemChange, errors: itemErrors, onValidate: handleValidateItem, onSubmit: updateItem, paymentRequest: paymentRequest }))] }) })] }));
};
